/**
 * The first-time walkthrough (docs/market-making-ux.md, Revision 1):
 *
 *   Explainer     three panels on the empty page / first visit, following the big number's states
 *                 (Draft → Working → Get out), each with a worked example from a live game
 *                 when one loads, and "Try it with paper" (opens the builder in Paper for that game
 *                 without touching the global trading mode)
 *   CoachTip      one tip at a time in the builder: margin → contracts → result → Place (Next/Skip)
 *   PostPlaceTip  on the tracking card after the first quote: Working → Get out → Locked
 *
 * The "seen" flag lives behind the source (localStorage, inside try/catch). The page's "?" replays it.
 */
import { useMemo, type ReactNode } from "react";
import {
  buildLadder,
  DEFAULT_CONTRACTS,
  DEFAULT_MARGIN_PCT,
  defaultPlan,
  quotableMarkets,
  quoteResult,
  type QuoteLeg,
} from "../../lib/marketMaking.js";
import { isGame, startMs, teamLabel, teamsOf } from "../../lib/marketsBoard.js";
import { fmtPrice } from "../../lib/format.js";
import { useOddsFormat } from "../../lib/odds.js";
import type { Ccy } from "../../lib/portfolio.js";
import { SoftCard } from "../kit.js";
import { btnBase, btnIdle, btnPrimary, fmtBid, gameLabel, money, signedMoney, type MarketMakingSource } from "./shared.js";

/* ===================================================================== coach */

export type CoachStep = "margin" | "contracts" | "result" | "place";
export const COACH_STEPS: CoachStep[] = ["margin", "contracts", "result", "place"];

export interface Coach {
  step: CoachStep | null;
  next: () => void;
  skip: () => void;
}

/** The highlight ring a coached control wears while its tip is showing. */
export const coachRing = (coach: Coach | undefined, step: CoachStep): string =>
  coach?.step === step ? "rounded-lg ring-2 ring-blue/60 ring-offset-2 ring-offset-bg" : "";

/** One tip, shown inline under its control while it's the current step. */
export function CoachTip({ coach, step, children }: { coach: Coach | undefined; step: CoachStep; children: ReactNode }) {
  if (!coach || coach.step !== step) return null;
  const i = COACH_STEPS.indexOf(step);
  const last = i === COACH_STEPS.length - 1;
  return (
    <div role="dialog" aria-label="Tip" className="gb-fade mt-2 rounded-lg border border-blue/40 bg-blue/10 p-2.5 text-[12px] leading-relaxed text-ink">
      <div>{children}</div>
      <div className="mt-2 flex items-center gap-2">
        <span className="nums text-[11px] text-faint">
          {i + 1} / {COACH_STEPS.length}
        </span>
        <button type="button" onClick={coach.skip} className="ml-auto min-h-8 px-2 text-[11px] text-dim hover:text-ink">
          Skip
        </button>
        <button type="button" onClick={coach.next} className="min-h-8 rounded-full border border-blue/50 bg-blue/15 px-3 text-[12px] font-medium text-blue hover:bg-blue/25">
          {last ? "Got it" : "Next"}
        </button>
      </div>
    </div>
  );
}

/** After the first quote is placed, on its tracking card. */
export function PostPlaceTip({ onDone }: { onDone: () => void }) {
  return (
    <div role="note" className="gb-fade mt-2 rounded-lg border border-blue/40 bg-blue/10 p-2.5 text-[12px] leading-relaxed text-ink">
      <p>
        Your quote is <span className="font-semibold text-blue">Working</span>: the bids are waiting for someone to sell to you. If one side
        fills more than the other, the big number becomes <span className="font-semibold">Get out now</span> (red for a loss, green for a gain):
        what you'd lock in by buying the other side straight away. Press <span className="font-semibold">Get out</span> to do it, or wait for
        your other bid. Equal fills show <span className="font-semibold text-up">Locked</span>.
      </p>
      <div className="mt-2 flex justify-end">
        <button type="button" onClick={onDone} className="min-h-8 rounded-full border border-blue/50 bg-blue/15 px-3 text-[12px] font-medium text-blue hover:bg-blue/25">
          Got it
        </button>
      </div>
    </div>
  );
}

/* ================================================================= explainer */

export interface Example {
  contestId: string | null;
  game: string;
  labels: [string, string];
  /** Each side's market now (0–1) + its American string. */
  market: [{ price: number; american: string | null }, { price: number; american: string | null }];
  bids: [number, number];
  contracts: number;
  both: number;
  /** Only side 0 fills: [if side 0 wins, if side 1 wins]. */
  only0: [number, number];
  cost: number;
  live: boolean;
}

const FALLBACK: Example = {
  contestId: null,
  game: "Hawks @ Heat",
  labels: ["Heat", "Hawks"],
  market: [{ price: 0.53, american: null }, { price: 0.49, american: null }],
  bids: [51, 47],
  contracts: DEFAULT_CONTRACTS,
  both: 0.2,
  only0: [4.9, -5.1],
  cost: 9.8,
  live: false,
};

/** A worked example from the soonest open game with a quotable moneyline (paper, 2%, 10 contracts). */
export function useExample(source: MarketMakingSource, enabled: boolean): Example {
  const games = source.useGames("");
  const pick = useMemo(() => {
    if (!enabled) return undefined;
    const now = Date.now();
    return games.items
      .filter((c) => isGame(c) && c.has_markets !== false && teamsOf(c) != null && startMs(c) >= now - 2 * 3_600_000)
      .sort((a, b) => startMs(a) - startMs(b))[0];
  }, [games.items, enabled]);
  const gb = source.useGameBook(pick?.id);
  return useMemo(() => {
    if (!pick || !gb.positions) return FALLBACK;
    const ml = quotableMarkets(gb.positions, gb.markets ?? []).find((m) => m.shape === "moneyline");
    const book = ml?.books.find((b) => b.key === ml.defaultBook) ?? ml?.books[0];
    const ladder = book && ml ? buildLadder(book, ml.shape, { mode: "paper", allowed: null, limitVenues: null }) : null;
    const d = ladder ? defaultPlan(ladder, "paper", DEFAULT_MARGIN_PCT) : null;
    if (!ladder || !d) return FALLBACK;
    const t = teamsOf(pick);
    const legs: QuoteLeg[] = ([0, 1] as const).map((col) => ({
      key: String(col),
      col,
      sideLabel: ladder.columns[col].label,
      partnerId: d.venues[col].partnerId,
      partnerName: d.venues[col].partnerName,
      positionHash: ladder.columns[col].positionHash,
      liquidityHash: d.venues[col].liquidityHash,
      cents: d.plan.cents[col],
      contracts: DEFAULT_CONTRACTS,
      feeRate: 0,
    }));
    const r = quoteResult(legs);
    return {
      contestId: pick.id,
      game: t ? `${teamLabel(t.away)} @ ${teamLabel(t.home)}` : gameLabel(pick),
      labels: [ladder.columns[0].label, ladder.columns[1].label],
      market: [
        { price: d.venues[0].ask, american: d.venues[0].american },
        { price: d.venues[1].ask, american: d.venues[1].american },
      ],
      bids: d.plan.cents,
      contracts: DEFAULT_CONTRACTS,
      both: Math.min(r.bothFill[0], r.bothFill[1]),
      only0: r.onlyFill[0],
      cost: r.cost,
      live: true,
    };
  }, [pick, gb.positions, gb.markets]);
}

/** The 3-panel explainer. */
export function Explainer({
  source,
  ccy,
  hasQuotes,
  onTryPaper,
  onNew,
  onDismiss,
}: {
  source: MarketMakingSource;
  ccy: Ccy;
  hasQuotes: boolean;
  onTryPaper: (contestId: string | null) => void;
  onNew: () => void;
  onDismiss?: () => void;
}) {
  const { format } = useOddsFormat();
  const ex = useExample(source, true);
  const [a, b] = ex.labels;
  const m = (i: 0 | 1) => fmtPrice(ex.market[i].price, ex.market[i].american, format);
  // Examples are paper (ATLAS) maths but read as dollars: the point is the shape, not the currency.
  const $ = (n: number) => money(n, "USD");
  const s$ = (n: number) => signedMoney(n, "USD");
  const pairCost = (ex.bids[0] + ex.bids[1]) / 100;
  const panels: { title: string; body: ReactNode }[] = [
    {
      title: "Draft: bid both sides, below the market",
      body: (
        <>
          {a} trades at <b className="nums">{m(0)}</b> and {b} at <b className="nums">{m(1)}</b>. You pick a margin and greenbook bids{" "}
          <b className="nums text-amber">{fmtBid(ex.bids[0], format)}</b> and <b className="nums text-amber">{fmtBid(ex.bids[1], format)}</b>. The
          amber number is what you'd make: <b className="nums text-amber">{s$(ex.both)}</b>.
        </>
      ),
    },
    {
      title: "Working: wait for both to fill",
      body: (
        <>
          Once placed, the number turns <b className="text-blue">blue</b> while your bids wait. A pair (one {a} + one {b}) costs{" "}
          <b className="nums">{$(pairCost)}</b> and always pays <b className="nums">$1.00</b>, so if both fill you keep{" "}
          <b className="nums text-up">{s$(ex.both)}</b> on {ex.contracts} a side, whoever wins: <b className="text-up">Locked</b>.
        </>
      ),
    },
    {
      title: "Only one filled? Get out",
      body: (
        <>
          Only {a} fills: <b className={`nums ${ex.only0[0] >= 0 ? "text-up" : "text-down"}`}>{s$(ex.only0[0])}</b> if {a} wins,{" "}
          <b className={`nums ${ex.only0[1] >= 0 ? "text-up" : "text-down"}`}>{s$(ex.only0[1])}</b> if {b} wins. The card then shows{" "}
          <b>Get out now</b>: what you'd lock in by buying {b} at today's price. <b>Get out</b> does it in one step (you press it), or you wait for
          your {b} bid.
        </>
      ),
    },
  ];
  return (
    <SoftCard className="p-3 lg:p-4">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <h2 className="text-[14px] font-semibold text-ink">How market making works</h2>
        <span className="text-[11px] text-faint">
          {ex.live ? `Live example: ${ex.game}, ${ex.contracts} contracts a side, ${DEFAULT_MARGIN_PCT}% margin` : "Example numbers"}
        </span>
        {onDismiss && (
          <button type="button" onClick={onDismiss} className="ml-auto min-h-8 px-1 text-[11px] text-faint hover:text-ink" aria-label="Hide the explainer">
            ✕
          </button>
        )}
      </div>
      <ol className="mt-2 grid gap-2 sm:grid-cols-3">
        {panels.map((p, i) => (
          <li key={p.title} className="rounded-md border border-border/70 bg-panel p-2.5">
            <div className="flex items-baseline gap-1.5">
              <span className="nums grid h-5 w-5 shrink-0 place-items-center rounded-full bg-amber/15 text-[11px] font-semibold text-amber">{i + 1}</span>
              <span className="text-[13px] font-semibold leading-snug text-ink">{p.title}</span>
            </div>
            <p className="mt-1 text-[12px] leading-relaxed text-dim">{p.body}</p>
          </li>
        ))}
      </ol>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => onTryPaper(ex.contestId)} className={`${btnBase} ${btnPrimary} font-semibold`}>
          Try it with paper{ex.live ? ` on ${ex.game}` : ""}
        </button>
        <button type="button" onClick={onNew} className={`${btnBase} ${btnIdle}`}>
          + New quote
        </button>
        <span className="text-[11px] text-faint">
          {hasQuotes
            ? "Only quotes made on this screen in this browser are listed."
            : ccy === "ATLAS"
              ? "No paper quotes yet. Paper is free practice money."
              : "No real quotes yet. Only quotes made on this screen in this browser are listed."}
        </span>
      </div>
    </SoftCard>
  );
}
